// src/pages/auth/Login.jsx
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Mail, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next'; 
import { sendLoginOtp } from "../../../services/auth.service";
import { EncryptionUtil } from "../../../utils/encryption.util";

export function Login() {
  const { t, i18n } = useTranslation('auth');
  const [form, setForm] = useState({ email: '', remember: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.email) errs.email = t('login.email_required');
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = t('login.email_invalid');
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    try {
      setLoading(true);
      const encryptedEmail = EncryptionUtil.encrypt(form.email);
      const preferredLanguage = (i18n.resolvedLanguage || i18n.language || localStorage.getItem('selectedLanguage') || 'en').split(/[-_]/)[0];
      await sendLoginOtp(encryptedEmail, preferredLanguage);
      navigate("/auth/login-otp", {
        state: {
          email: encryptedEmail,
          displayEmail: form.email,
        },
      });
    } catch (err: any) {
      setErrors({
        email: err?.response?.data?.message || "Failed to send OTP.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="w-full max-w-md text-center">
        <div className="bg-white rounded-2xl p-10 shadow-xl border border-[#E5E7EB]">
          <div className="w-16 h-16 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-[#22C55E]" />
          </div>
          <h2 className="text-2xl text-[#2D323A] mb-2">{t('login.success.title')}</h2>
          <p className="text-[#717182]">{t('login.success.description')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      <div className="bg-white rounded-2xl p-8 shadow-xl border border-[#E5E7EB]">
        <div className="text-center mb-8">
          <h1 className="text-3xl text-[#2D323A] mb-2">{t('login.title')}</h1>
          <p className="text-[#717182]">{t('login.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm text-[#2D323A] mb-1.5">
              {t('login.email')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder={t('login.email_placeholder')}
                className={`w-full pl-11 pr-4 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none focus:border-[#1E6DFF] transition-colors ${errors.email ? 'border-[#EF4444]' : 'border-[#E5E7EB]'}`}
              />
            </div>
            {errors.email && (
              <p className="flex items-center gap-1 text-[#EF4444] text-sm mt-1">
                <AlertCircle className="w-4 h-4" /> {errors.email}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input 
              type="checkbox" 
              id="remember" 
              checked={form.remember} 
              onChange={e => setForm({ ...form, remember: e.target.checked })} 
              className="w-4 h-4 accent-[#1E6DFF] rounded" 
            />
            <label htmlFor="remember" className="text-sm text-[#717182]">
              {t('login.remember')}
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            {loading ? t('login.button_loading') : t('login.button')}
          </button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[#E5E7EB]" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-4 text-sm text-[#717182]">
              {t('login.or_continue')}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button className="flex items-center justify-center gap-2 py-2.5 border border-[#E5E7EB] rounded-xl hover:bg-[#F5F7FA] transition-colors text-sm text-[#2D323A]">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('login.google')}
          </button>
          <button className="flex items-center justify-center gap-2 py-2.5 border border-[#E5E7EB] rounded-xl hover:bg-[#F5F7FA] transition-colors text-sm text-[#2D323A]">
            <svg className="w-5 h-5 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            {t('login.facebook')}
          </button>
        </div>

        <p className="text-center text-sm text-[#717182] mt-6">
          {t('login.no_account')}{' '}
          <Link to="/auth/register" className="text-[#1E6DFF] hover:underline">
            {t('login.sign_up')}
          </Link>
        </p>
      </div>
    </div>
  );
}
